import type { Icon } from '@phosphor-icons/react';
import { FileIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { RESOURCE_TYPE_META } from 'constants/resourceTypeMeta';
import { format, isThisYear } from 'date-fns';
import { ResourceType } from 'gen/api';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import type { Notification } from '../../api/notifications/fetchNotifications';
import { getResourcePath } from '../../utils/getResourcePath';
import { getInitialsFromString } from '../../utils/helpers';
import s from './SharedWithMeDropdown.module.scss';

type SharedWithMeItemT = {
  notification: Notification;
  setIsOpen: (isOpen: boolean) => void;
};

interface IResourceMeta {
  label: string;
  icon?: Icon;
}

const AVATAR_COLOURS = [
  'blue',
  'purple',
  'green',
  'orange',
  'cyan',
  'crimson',
  'yellow',
  'amber',
  'red',
  'grey',
] as const;

const NOTIFICATION_RESOURCE_META: Partial<Record<ResourceType, IResourceMeta>> = {
  ...RESOURCE_TYPE_META,
  [ResourceType.FTO]: { label: 'FTO' },
  [ResourceType.INFRINGEMENT_DETECTION]: { label: 'Infringement Detection' },
  [ResourceType.AI_SEARCH]: { label: 'AI Search' },
};

const getResourceMeta = (resourceType: ResourceType): IResourceMeta =>
  NOTIFICATION_RESOURCE_META[resourceType] ?? { label: resourceType };

const getAvatarColour = (username: string) => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = (Math.imul(hash, 31) + username.charCodeAt(i)) | 0;
  }
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
};

const formatNotificationDate = (date: string) => format(date, isThisYear(date) ? 'd MMM' : 'd MMM y');

export const SharedWithMeItem: FC<SharedWithMeItemT> = ({ notification, setIsOpen }) => {
  'use memo';

  const navigate = useNavigate();

  const { resourceType, resourceId, resourceTitle, sharedByUserInfo } =
    notification.notificationContent.resourceSharedNotification;
  const link = getResourcePath(resourceType, resourceId);
  const { label, icon: ResourceIcon = FileIcon } = getResourceMeta(resourceType);

  const handleClick = () => {
    if (!link) return;
    void navigate(link);
    setIsOpen(false);
  };

  const content = (
    <>
      <span className={s.itemHeader}>
        <span className={s.avatar} data-colour={getAvatarColour(sharedByUserInfo.username)} aria-hidden="true">
          {getInitialsFromString(sharedByUserInfo.username)}
        </span>
        <span className={s.message}>
          <span className={s.username}>{sharedByUserInfo.username}</span> shared with you
        </span>
        <span className={s.date}>{formatNotificationDate(notification.createdAt)}</span>
      </span>
      <span className={s.itemBody}>
        <span className={s.card}>
          <span className={s.cardIcon}>
            <ResourceIcon size={16} weight="bold" />
          </span>
          <span className={s.cardDetails}>
            <span className={s.cardTitle}>{resourceTitle?.trim() || 'Untitled'}</span>
            <span className={s.cardType}>{label}</span>
          </span>
        </span>
      </span>
    </>
  );

  if (!link) {
    return <div className={s.notificationItem}>{content}</div>;
  }

  return (
    <button type="button" className={classNames(s.notificationItem, s.clickable)} onClick={handleClick}>
      {content}
    </button>
  );
};
